// 頁首範圍切換器（SPEC #vms-web-220 頂列「範圍切換器」，VMS UX 評審 2026-10-13 P0-3；取代原本的 <select> 平清單）：
//   - 按鈕 = 路徑麵包屑「EddyStudio › lab」＋右側小字「14 台」（你可存取的設備數，不含 NVR 通道）；「全部」寫「全部（3 個組織）」
//   - 面板：最上方搜尋（至少 2 字，比對名稱與路徑；另外問伺服器 /v1/me/tree?q= 找組織樹的節點）；分組「我自己的／組織授權給我／別人分享給我／檢視」；
//     每列名稱與設備數，同名的列在下方小字顯示路徑；點一列 = 切範圍並關閉；Esc 關閉、上下鍵選、Enter 選定
//   - 底部固定〔＋ 建立組織〕（組織設定同一個對話框）
//   - 跟左側組織樹讀寫同一個範圍（lib/scope.tsx）；選了組織樹的節點時通知左樹展開到那裡（requestTreeReveal）
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useScope } from '../../lib/scope';
import { nodePath, type ScopeNode } from '../../lib/myDevices';
import { meTreeApi, type MeTreeItem } from '../../lib/orgTreeApi';
import { isRemoteScope, scopeKeyOf } from '../../lib/remoteScope';
import { useViewsAndGroups } from '../orgtree/hooks';
import { requestTreeReveal } from '../orgtree/treeReveal';
import { scopeNodeName } from './Shell';

type T = (k: string, o?: Record<string, unknown>) => string;

export interface PickRow {
  key: string;
  name: string;
  /** 路徑（不含自己；同名或搜尋結果時顯示） */
  path: string;
  count?: number;
  group: string;
  depth: number;
  disabled?: boolean;
  note?: string;
  /** 組織樹節點：路徑上每一層的 key（給左樹展開） */
  reveal?: string[];
}

/** 範圍樹 → 面板的列（來源節點當分組標題、不當列）；同名的列帶路徑。 */
export function pickRows(t: T, tree: ScopeNode[]): PickRow[] {
  const rows: PickRow[] = [];
  for (const n of tree) {
    if (n.kind === 'source') continue;
    const path = nodePath(tree, n.key)
      .slice(0, -1)
      .filter((x) => x.kind !== 'source')
      .map((x) => scopeNodeName(t, x))
      .join(' › ');
    rows.push({
      key: n.key,
      name: scopeNodeName(t, n),
      path,
      count: n.kind === 'org' && n.notice ? undefined : n.count,
      group: t('scope.src_' + n.source),
      depth: n.depth - 1,
      disabled: !!n.notice,
      note: n.notice ? t(`scope.notice_${n.notice}`, { defaultValue: n.notice }) : undefined,
    });
  }
  return rows;
}

/** 名稱出現不只一次的（同名家戶 / 場域要看路徑才分得出來）。 */
export function duplicateNames(rows: Pick<PickRow, 'name'>[]): Set<string> {
  const seen = new Map<string, number>();
  for (const r of rows) seen.set(r.name, (seen.get(r.name) ?? 0) + 1);
  return new Set([...seen].filter(([, n]) => n > 1).map(([k]) => k));
}

/** 搜尋：名稱或路徑含有輸入（不分大小寫）。 */
export function matchRow(r: Pick<PickRow, 'name' | 'path'>, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return r.name.toLowerCase().includes(s) || r.path.toLowerCase().includes(s) || `${r.path} › ${r.name}`.toLowerCase().includes(s);
}

/** 按鈕上的麵包屑。 */
/** 「全部」的計數寫清楚算什麼（第三輪 B3-16：「全部（2 個組織）」底下列了家 + 2 個組織）：組織（team）與家（個人組織）分開數。 */
export function allLabel(t: T, tree: ScopeNode[]): string {
  const tops = tree.filter((n) => n.kind === 'org' && (n.count > 0 || !!n.notice));
  const teams = tops.filter((n) => n.orgKind !== 'personal').length;
  const homes = tops.length - teams;
  return homes ? t('scope.all_n_orgs_homes', { count: teams, homes }) : t('scope.all_n_orgs', { count: teams });
}

export function scopeCrumb(t: T, tree: ScopeNode[], scope: string, remoteLabel: string, orgCount: number): string {
  if (scope === 'all') return orgCount >= 0 ? allLabel(t, tree) : t('scope.all_n_orgs', { count: orgCount });
  if (isRemoteScope(scope)) return remoteLabel || t('ot.scope_custom');
  const p = nodePath(tree, scope);
  if (!p.length) return t('ot.scope_custom');
  const named = p.filter((x) => x.kind !== 'source');
  return (named.length ? named : p).map((x) => scopeNodeName(t, x)).join(' › ');
}

export function ScopePicker() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { tree, scope, setScope, scopeLabel, visibleDevices, planes, orgs, booting } = useScope();
  const { views } = useViewsAndGroups(planes, orgs.filter((o) => o.kind === 'team'));
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  // 只算有設備（或有狀況要說明）的組織；每個帳號都有的空個人組織不算
  const orgCount = tree.filter((n) => n.kind === 'org' && (n.count > 0 || !!n.notice)).length;
  const crumb = scopeCrumb(t, tree, scope, scopeLabel, orgCount);
  const count = visibleDevices.length;

  const base = useMemo(() => {
    const rows = pickRows(t, tree);
    for (const v of views.data ?? []) rows.push({ key: 'view:' + v.view_id, name: t('ot.view_label', { name: v.name }), path: '', group: t('ot.tab_views'), depth: 0 });
    return rows;
  }, [t, tree, views.data]);
  const dup = useMemo(() => duplicateNames(base), [base]);

  const sq = q.trim();
  const remote = useQuery({
    queryKey: ['me-tree', 'q', sq],
    queryFn: async () => {
      const rs = await Promise.all(planes.map((p) => meTreeApi.tree(p, { q: sq, limit: 50 }).then((r) => r.items.map((x) => ({ ...x, _api: p.api })))));
      return rs.flat() as (MeTreeItem & { _api: string })[];
    },
    enabled: open && sq.length >= 2 && planes.length > 0,
    staleTime: 30_000,
    retry: false,
  });
  const rows = useMemo(() => {
    if (sq.length < 2) return base;
    const local = base.filter((r) => matchRow(r, sq));
    const have = new Set(local.map((r) => r.key));
    // 同一個節點在「我自己的」與「組織樹」各出現一次（第三輪 B3-16）：伺服器搜到的節點跟本地某一列同名、而且上一層也同名就不再列
    const last = (p: string) => p.split(' › ').filter(Boolean).slice(-1)[0] ?? '';
    const localSig = new Set(local.map((r) => r.name + '|' + last(r.path)));
    const extra: PickRow[] = [];
    for (const it of remote.data ?? []) {
      if (it.kind === 'device') continue;
      const org = it.path?.find((p) => p.key.startsWith('org:'))?.key.slice(4);
      const key = scopeKeyOf(it, org ?? (it.kind === 'org' ? it.id : undefined));
      if (have.has(key) || key === 'all') continue;
      have.add(key);
      const path = (it.path ?? []).map((p) => p.name).join(' › ');
      // 組織樹節點 = 範圍樹的場域（node:<org>:<id> ↔ site:<id>）；同一個節點不列兩次
      if ((it.kind === 'node' && have.has('site:' + it.id)) || localSig.has(it.name + '|' + last(path))) continue;
      extra.push({ key, name: it.name, path, count: it.device_count, group: t('ot.tab_tree'), depth: 0, reveal: (it.path ?? []).map((p) => p.key) });
    }
    return [...local, ...extra];
  }, [base, sq, remote.data, t]);
  const selectable = rows.filter((r) => !r.disabled);

  useEffect(() => {
    if (!open) return;
    setActive(0);
    const off = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', off);
    input.current?.focus(); // 面板 commit 後同步聚焦（不等下一輪），打開後立刻打字不丟字（第二輪 B2-31）
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  useEffect(() => setActive(0), [sq]);

  const pick = (r: PickRow | undefined) => {
    if (!r || r.disabled) return;
    const label = isRemoteScope(r.key) ? [r.path, r.name].filter(Boolean).join(' › ') : '';
    setScope(r.key, label);
    if (r.reveal) requestTreeReveal(r.reveal);
    setOpen(false);
    setQ('');
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(0, selectable.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(selectable[active]);
    }
  };

  let lastGroup = '';
  return (
    <div className="scope-picker" ref={wrap}>
      <button
        type="button"
        className="scope-btn"
        data-testid="scope-picker"
        data-scope={scope}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('shell.scope') + t('common.colon') + crumb}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="scope-crumb">{booting ? t('scope.loading') : crumb}</span>
        {!booting && (
          <span className="scope-count muted small" title={t('scope.count_tip')}>
            {t('scope.count', { count })}
          </span>
        )}
        <span aria-hidden>▾</span>
      </button>
      {open && (
        <div className="scope-panel card" role="dialog" aria-label={t('shell.scope')} onKeyDown={onKey} data-testid="scope-panel">
          <input
            ref={input}
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('scope.search')}
            aria-label={t('scope.search')}
          />
          {sq.length === 1 && <p className="muted small">{t('ot.search_min')}</p>}
          <ul className="scope-list" role="listbox" aria-label={t('shell.scope')}>
            {sq.length < 2 && (
              <li role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={scope === 'all'}
                  className={'scope-row' + (scope === 'all' ? ' sel' : '')}
                  data-scope-key="all"
                  onClick={() => pick({ key: 'all', name: '', path: '', group: '', depth: 0 })}
                >
                  <span className="scope-row-name">{allLabel(t, tree)}</span>
                </button>
              </li>
            )}
            {rows.map((r) => {
              const head = r.group !== lastGroup ? r.group : '';
              lastGroup = r.group;
              const idx = selectable.indexOf(r);
              const showPath = !!r.path && (dup.has(r.name) || sq.length >= 2);
              return [
                head && (
                  <li key={'g-' + r.key} className="scope-group" role="presentation">
                    {head}
                  </li>
                ),
                <li key={r.key} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={scope === r.key}
                    aria-disabled={r.disabled || undefined}
                    disabled={r.disabled}
                    className={'scope-row' + (scope === r.key ? ' sel' : '') + (idx === active && idx >= 0 ? ' active' : '')}
                    style={{ paddingLeft: 8 + Math.max(0, r.depth) * 14 }}
                    data-scope-key={r.key}
                    onClick={() => pick(r)}
                    onMouseEnter={() => idx >= 0 && setActive(idx)}
                  >
                    <span className="scope-row-main">
                      <span className="scope-row-name">{r.name}</span>
                      {r.note && <span className="muted small"> · {r.note}</span>}
                      {showPath && <span className="scope-row-path muted small">{r.path}</span>}
                    </span>
                    {r.count !== undefined && (
                      <span className="scope-row-count muted small" title={t('scope.count_tip')}>
                        {r.count}
                      </span>
                    )}
                  </button>
                </li>,
              ];
            })}
            {sq.length >= 2 && remote.isFetching && <li className="muted small">{t('common.loading')}</li>}
            {sq.length >= 2 && !remote.isFetching && rows.length === 0 && <li className="muted small">{t('ot.no_results')}</li>}
          </ul>
          <div className="scope-panel-foot">
            <button
              type="button"
              className="btn btn-sm"
              data-testid="scope-create-org"
              onClick={() => {
                setOpen(false);
                nav('/org?create=1');
              }}
            >
              {t('scope.create_org')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
